import { Component } from '@theme/component';

/**
 * @typedef {Object} FilterBarRefs
 * @property {never} _unused
 */

/** @extends {Component<FilterBarRefs>} */
class FilterBarComponent extends Component {
  /** @type {AbortController|null} */
  #drawerController = null;

  connectedCallback() {
    super.connectedCallback();
    this.#bindDrawer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#drawerController?.abort();
  }

  /**
   * Proxy filter pill / swatch clicks to hidden native inputs (filter strip).
   * Called with on:click on each individual button, so event.target IS the button.
   * @param {MouseEvent} event
   */
  handleControlClick(event) {
    const el = /** @type {HTMLElement} */ (event.target);
    if (!el.dataset.filter || !el.dataset.value) return;
    this.#proxyFilterInput(el.dataset.filter, el.dataset.value);
  }

  /**
   * Clear filters through Horizon's section renderer without a page reload.
   * @param {MouseEvent} event
   */
  handleClearFilters(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const clear = target.closest('[data-clear-filters]');
    if (!(clear instanceof HTMLAnchorElement)) return;

    event.preventDefault();
    this.#clearFiltersByURL(clear.href);
  }

  /** Attach click delegation to the drawer (outside this component's subtree). */
  #bindDrawer() {
    this.#drawerController?.abort();
    this.#drawerController = new AbortController();
    const sectionId = this.dataset.section;
    const drawer = document.getElementById(`FilterBarDrawer-${sectionId}`);
    if (!drawer) return;

    drawer.addEventListener('click', (/** @type {MouseEvent} */ event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const clear = target?.closest?.('[data-clear-filters]');
      if (clear instanceof HTMLAnchorElement) {
        event.preventDefault();
        this.#clearFiltersByURL(clear.href);
        this.#closeFilterModal(drawer);
        return;
      }
      const filter = target?.closest?.('[data-filter][data-value]');
      if (filter instanceof HTMLElement) {
        this.#proxyFilterInput(filter.dataset.filter ?? '', filter.dataset.value ?? '');
        return;
      }
      const sort = target?.closest?.('[data-sort]');
      if (sort instanceof HTMLElement) {
        this.#updateModalSortState(sort);
        this.#proxySortInput(sort.dataset.sort ?? '');
      }
    }, { signal: this.#drawerController.signal });
  }

  /** @param {string} url */
  #clearFiltersByURL(url) {
    const sectionId = this.dataset.section;
    const facetsForm = /** @type {(HTMLElement & { updateFiltersByURL?: (url: string) => void }) | null} */ (document.querySelector(
      `facets-form-component[section-id="${sectionId}"]`
    ));
    facetsForm?.updateFiltersByURL?.(url);
  }

  /** @param {HTMLElement} modalComponent */
  #closeFilterModal(modalComponent) {
    const closeDialog = /** @type {undefined | (() => void)} */ (modalComponent.closeDialog);
    if (typeof closeDialog === 'function') {
      closeDialog.call(modalComponent);
      return;
    }
    modalComponent.querySelector('dialog')?.close();
  }

  /**
   * @param {string} filterParam
   * @param {string} value
   */
  #proxyFilterInput(filterParam, value) {
    const sectionId = this.dataset.section;
    const input = /** @type {HTMLElement|null} */ (document.querySelector(
      `facets-form-component[section-id="${sectionId}"] input[name="${filterParam}"][value="${value}"]`
    ));
    input?.click();
  }

  /** @param {HTMLElement} selectedSort */
  #updateModalSortState(selectedSort) {
    const sortValue = selectedSort.dataset.sort;
    const sortGroup = selectedSort.closest('[data-section]') ?? selectedSort.closest('.filter-modal__sort');
    sortGroup?.querySelectorAll('[data-sort]').forEach((sort) => {
      const isSelected = sort instanceof HTMLElement && sort.dataset.sort === sortValue;
      sort.classList.toggle('is-selected', isSelected);
      sort.setAttribute('aria-pressed', String(isSelected));
    });
  }

  /** @param {string} sortValue */
  #proxySortInput(sortValue) {
    const sectionId = this.dataset.section;
    const radio = /** @type {HTMLElement|null} */ (document.querySelector(
      `facets-form-component[section-id="${sectionId}"] input[name="sort_by"][value="${sortValue}"]`
    ));
    if (radio) {
      radio.click();
      return;
    }
    const select = /** @type {HTMLSelectElement|null} */ (document.querySelector(
      `facets-form-component[section-id="${sectionId}"] select[name="sort_by"]`
    ));
    if (select) {
      select.value = sortValue;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

customElements.define('filter-bar-component', FilterBarComponent);
